// MOMERY CUSTOM — FAC dynamic cart pricing updates.
(function (root) {
    if (root.FacDynamicPricingCartUpdate) {
        return;
    }

    const STATE_IDLE = 'idle';
    const STATE_PENDING = 'pending';
    const STATE_UPDATING = 'updating';
    const STATE_BLOCKED = 'blocked';
    const UPDATE_ERROR_MESSAGE = 'Die Menge konnte nicht aktualisiert werden. Bitte versuche es erneut.';
    const BLOCKED_ERROR_MESSAGE = 'Der Warenkorb konnte nicht sicher aktualisiert werden. Bitte lade die Seite neu.';
    const SECTION_NAMES = ['main-cart', 'cart-drawer', 'cart-bubble'];
    const DRAWER_SECTION_NAMES = ['cart-drawer', 'cart-bubble'];
    const UPDATE_ORIGIN_CART_PAGE = 'cart-page';
    const UPDATE_ORIGIN_CART_DRAWER = 'cart-drawer';
    const TRANSITION_NONE_TO_NONE = 'NONE_TO_NONE';
    const TRANSITION_NONE_TO_SURCHARGE = 'NONE_TO_SURCHARGE';
    const TRANSITION_SURCHARGE_TO_NONE = 'SURCHARGE_TO_NONE';
    const TRANSITION_SAME_SURCHARGE = 'SAME_SURCHARGE';
    const TRANSITION_REPLACE_SURCHARGE = 'REPLACE_SURCHARGE';
    const SECTION_REFRESH_MAX_ATTEMPTS = 3;
    const SECTION_REFRESH_RETRY_DELAY_MS = 150;
    const BASE_PRINTESS_PROPERTIES = [
        '_printessSaveToken',
        '_printessThumbnail',
        '_printessSettings',
        '_printessPageCount',
        '_printessMinPages',
        '_printessAdditionalPages',
        '_printessFormFields',
        '_fac_printess_template_id',
        '_fac_printess_template_name',
    ];

    let state = STATE_IDLE;
    let activeUpdate = null;
    let guardsInstalled = false;
    let latestUpdateGeneration = 0;
    let sectionRefreshNonce = 0;
    let fullReloadTriggered = false;

    function fail(message) {
        throw new Error(message);
    }

    function sectionRefreshError(message, details = {}) {
        const error = new Error(message);
        error.facSectionRefresh = true;
        error.facPhase = details.phase || 'section-refresh';
        error.facSectionName = details.sectionName || null;
        error.facGroupMatch = details.groupMatch ?? null;
        error.facKeyMatch = details.keyMatch ?? null;

        return error;
    }

    function logSectionRefresh(details) {
        console.warn('[FAC_CART] Cart section refresh', {
            generation: details.generation,
            retryAttempt: details.retryAttempt,
            phase: details.phase,
            sectionName: details.sectionName ?? null,
            groupMatch: details.groupMatch ?? null,
            keyMatch: details.keyMatch ?? null,
            cartStillMatchesFinalState: details.cartStillMatchesFinalState ?? null,
        });
    }

    function nextUpdateGeneration() {
        latestUpdateGeneration += 1;

        return latestUpdateGeneration;
    }

    function isCurrentGeneration(generation) {
        return generation === latestUpdateGeneration;
    }

    function assertCurrentGeneration(generation) {
        if (!isCurrentGeneration(generation)) {
            const error = new Error('FAC cart section response belongs to a stale update generation.');
            error.facStaleGeneration = true;

            throw error;
        }
    }

    function requireNonEmptyString(value, fieldName) {
        if (typeof value !== 'string' || value.length === 0) {
            fail(`Missing or invalid ${fieldName}.`);
        }

        return value;
    }

    function normalizeIdentifier(value, fieldName) {
        if (typeof value === 'number') {
            if (!Number.isSafeInteger(value) || value <= 0) {
                fail(`Missing or invalid ${fieldName}.`);
            }

            return String(value);
        }

        if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim();
        }

        fail(`Missing or invalid ${fieldName}.`);
    }

    function parseInteger(value, fieldName, minimum) {
        if (value === '' || value === null || value === undefined) {
            fail(`Missing or invalid ${fieldName}.`);
        }

        const parsedValue = Number(value);

        if (!Number.isInteger(parsedValue) || parsedValue < minimum) {
            fail(`Missing or invalid ${fieldName}.`);
        }

        return parsedValue;
    }

    function requireUpdateOrigin(value) {
        if (value !== UPDATE_ORIGIN_CART_PAGE && value !== UPDATE_ORIGIN_CART_DRAWER) {
            fail('Missing or invalid FAC cart update origin.');
        }

        return value;
    }

    function sectionNamesForOrigin(origin) {
        return requireUpdateOrigin(origin) === UPDATE_ORIGIN_CART_DRAWER
            ? DRAWER_SECTION_NAMES
            : ['main-cart', 'cart-bubble'];
    }

    function sectionsUrlForOrigin(origin) {
        return requireUpdateOrigin(origin) === UPDATE_ORIGIN_CART_DRAWER
            ? root.location.pathname
            : theme.routes.cart_url;
    }

    function itemProperties(item) {
        return item && typeof item.properties === 'object' && item.properties !== null
            ? item.properties
            : {};
    }

    function isFacBase(item) {
        const properties = itemProperties(item);

        return properties._fac_is_base === '1' && properties._fac_is_surcharge !== '1';
    }

    function isFacSurcharge(item) {
        const properties = itemProperties(item);

        return properties._fac_is_surcharge === '1' && properties._fac_is_base !== '1';
    }

    function resolvePair(cart, cartGroupId, baseKey = '') {
        requireNonEmptyString(cartGroupId, 'cart group ID');

        if (!cart || !Array.isArray(cart.items)) {
            fail('Invalid cart state.');
        }

        const groupItems = cart.items.filter((item) => {
            return itemProperties(item)._fac_cart_group_id === cartGroupId;
        });

        const baseItems = groupItems.filter(isFacBase);
        const surchargeItems = groupItems.filter(isFacSurcharge);

        if (
            baseItems.length !== 1 ||
            surchargeItems.length > 1 ||
            groupItems.length !== baseItems.length + surchargeItems.length
        ) {
            fail('Dynamic pricing cart group is missing or ambiguous.');
        }

        const baseItem = baseItems[0];

        if (baseKey && baseItem.key !== baseKey) {
            fail('Dynamic pricing base line no longer matches the selected cart line.');
        }

        return {
            baseItem,
            surchargeItem: surchargeItems[0] || null,
        };
    }

    function buildValidationRequest(baseItem, quantity, selectedOptions) {
        const newQuantity = parseInteger(quantity, 'quantity', 1);
        const properties = itemProperties(baseItem);
        const printessTemplateName = requireNonEmptyString(
            properties._fac_printess_template_name,
            'Printess template name'
        );
        const saveToken = requireNonEmptyString(properties._printessSaveToken, 'Printess save token');

        if (!saveToken.startsWith('st:')) {
            fail('Missing or invalid Printess save token.');
        }

        if (!root.FacCartPricing?.validateSelectedOptions) {
            fail('FAC selected option validation is unavailable.');
        }

        const canonicalSelectedOptions = root.FacCartPricing.validateSelectedOptions(
            selectedOptions
        );

        return {
            printess_template_name: printessTemplateName,
            shopify_product_id: normalizeIdentifier(baseItem.product_id, 'Shopify product ID'),
            shopify_variant_id: normalizeIdentifier(
                baseItem.variant_id ?? baseItem.id,
                'Shopify variant ID'
            ),
            quantity: newQuantity,
            save_token: saveToken,
            page_count: parseInteger(properties._printessPageCount, 'Printess page count', 0),
            min_pages: parseInteger(properties._printessMinPages, 'Printess minimum pages', 0),
            additional_pages: parseInteger(
                properties._printessAdditionalPages,
                'Printess additional pages',
                0
            ),
            selected_options: canonicalSelectedOptions,
        };
    }

    function parseExistingAuthorization(baseItem, cartGroupId) {
        const properties = itemProperties(baseItem);
        const pricingPayload = requireNonEmptyString(
            properties._fac_pricing_payload,
            'pricing payload'
        );
        let signedPayload;

        try {
            signedPayload = JSON.parse(pricingPayload);
        } catch (error) {
            fail('Invalid existing pricing authorization payload.');
        }

        if (!signedPayload || Array.isArray(signedPayload) || typeof signedPayload !== 'object') {
            fail('Invalid existing pricing authorization payload.');
        }

        if (!root.FacCartPricing?.validateSelectedOptions) {
            fail('FAC selected option validation is unavailable.');
        }

        const selectedOptions = root.FacCartPricing.validateSelectedOptions(
            signedPayload.selected_options
        );

        if (!root.FacCartPricing?.normalizePricingAuthorizationResult) {
            fail('FAC pricing authorization normalization is unavailable.');
        }

        const authorization = root.FacCartPricing.normalizePricingAuthorizationResult({
            dynamic_pricing_enabled: true,
            pricing_authorization_version: signedPayload.version,
            pricing_payload: pricingPayload,
            pricing_signature: properties._fac_pricing_signature,
            cart_group_id: signedPayload.cart_group_id,
            expires_at: signedPayload.expires_at,
            total_delta_cents: signedPayload.total_delta_cents,
            surcharge_variant_id: signedPayload.surcharge_variant_id,
            surcharge_quantity: signedPayload.surcharge_quantity,
            printess_template_id: signedPayload.printess_template_id,
        }, baseItem.quantity);

        if (
            authorization.cart_group_id !== cartGroupId ||
            properties._fac_cart_group_id !== authorization.cart_group_id ||
            properties._fac_pricing_expires_at !== String(authorization.expires_at) ||
            properties._fac_total_delta_cents !== String(authorization.total_delta_cents) ||
            properties._fac_printess_template_id !== String(authorization.printess_template_id)
        ) {
            fail('Existing pricing authorization does not match the base line.');
        }

        const hasSurchargeVariantProperty = Object.prototype.hasOwnProperty.call(
            properties,
            '_fac_surcharge_variant_id'
        );

        if (authorization.surcharge_variant_id === null) {
            if (hasSurchargeVariantProperty) {
                fail('Existing zero-surcharge authorization contains a surcharge variant.');
            }
        } else if (
            !hasSurchargeVariantProperty ||
            properties._fac_surcharge_variant_id !== authorization.surcharge_variant_id
        ) {
            fail('Existing surcharge variant does not match the authorization.');
        }

        return {
            ...authorization,
            selected_options: selectedOptions,
        };
    }

    function createSnapshot(pair, cartGroupId) {
        const baseProperties = itemProperties(pair.baseItem);
        const existingAuthorization = parseExistingAuthorization(pair.baseItem, cartGroupId);
        const needsSurcharge = existingAuthorization.surcharge_quantity > 0;

        if (needsSurcharge !== (pair.surchargeItem !== null)) {
            fail('Existing surcharge line does not match the authorization.');
        }

        const surchargeVariantId = pair.surchargeItem
            ? normalizeIdentifier(
                pair.surchargeItem.variant_id ?? pair.surchargeItem.id,
                'Surcharge variant ID'
            )
            : existingAuthorization.surcharge_variant_id;

        if (pair.surchargeItem) {
            const surchargeProperties = itemProperties(pair.surchargeItem);

            if (
                surchargeVariantId !== existingAuthorization.surcharge_variant_id ||
                pair.surchargeItem.quantity !== existingAuthorization.surcharge_quantity ||
                surchargeProperties._fac_pricing_signature !==
                    existingAuthorization.pricing_signature ||
                surchargeProperties._fac_pricing_expires_at !==
                    String(existingAuthorization.expires_at) ||
                surchargeProperties._fac_total_delta_cents !==
                    String(existingAuthorization.total_delta_cents) ||
                surchargeProperties._fac_printess_template_id !==
                    String(existingAuthorization.printess_template_id) ||
                surchargeProperties._fac_parent_quantity !== String(pair.baseItem.quantity) ||
                Object.prototype.hasOwnProperty.call(
                    surchargeProperties,
                    '_fac_pricing_payload'
                )
            ) {
                fail('Existing surcharge line does not match the authorization.');
            }
        }

        return {
            cartGroupId,
            authorizationVersion: existingAuthorization.pricing_authorization_version,
            authorization: existingAuthorization,
            surchargeVariantId,
            base: {
                key: pair.baseItem.key,
                quantity: pair.baseItem.quantity,
                productId: normalizeIdentifier(pair.baseItem.product_id, 'Shopify product ID'),
                variantId: normalizeIdentifier(
                    pair.baseItem.variant_id ?? pair.baseItem.id,
                    'Shopify variant ID'
                ),
                properties: { ...baseProperties },
            },
            surcharge: pair.surchargeItem ? {
                key: pair.surchargeItem.key,
                quantity: pair.surchargeItem.quantity,
                variantId: surchargeVariantId,
                properties: { ...itemProperties(pair.surchargeItem) },
            } : null,
        };
    }

    function validateAuthorizationResult(result, snapshot, cart, baseQuantity) {
        if (!result || result.dynamic_pricing_enabled !== true) {
            fail('Dynamic pricing validation was not enabled.');
        }

        if (!root.FacCartPricing?.normalizePricingAuthorizationResult) {
            fail('FAC pricing authorization normalization is unavailable.');
        }

        const authorization = root.FacCartPricing.normalizePricingAuthorizationResult(
            result,
            baseQuantity
        );

        if (authorization.cart_group_id === snapshot.cartGroupId) {
            fail('FAC did not issue a new cart group ID.');
        }

        if (cart.items.some((item) => {
            return itemProperties(item)._fac_cart_group_id === authorization.cart_group_id;
        })) {
            fail('The new cart group ID already exists.');
        }

        return authorization;
    }

    function surchargeTransition(snapshot, authorization) {
        const hadSurcharge = snapshot.surcharge !== null;
        const needsSurcharge = authorization.surcharge_quantity > 0;

        if (!hadSurcharge && !needsSurcharge) {
            return TRANSITION_NONE_TO_NONE;
        }

        if (!hadSurcharge && needsSurcharge) {
            return TRANSITION_NONE_TO_SURCHARGE;
        }

        if (!needsSurcharge) {
            return TRANSITION_SURCHARGE_TO_NONE;
        }

        return snapshot.surcharge.variantId === authorization.surcharge_variant_id
            ? TRANSITION_SAME_SURCHARGE
            : TRANSITION_REPLACE_SURCHARGE;
    }

    function applyOptionalTemplateProperties(properties, authorization) {
        if (
            authorization.printess_template_id !== undefined &&
            authorization.printess_template_id !== null
        ) {
            properties._fac_printess_template_id = String(authorization.printess_template_id);
        }

        if (
            typeof authorization.printess_template_name === 'string' &&
            authorization.printess_template_name.length > 0
        ) {
            properties._fac_printess_template_name = authorization.printess_template_name;
        }

        return properties;
    }

    function buildSurchargeProperties(snapshotProperties, authorization, baseQuantity) {
        const properties = {
            ...snapshotProperties,
            _fac_cart_group_id: authorization.cart_group_id,
            _fac_pricing_signature: authorization.pricing_signature,
            _fac_pricing_expires_at: String(authorization.expires_at),
            _fac_total_delta_cents: String(authorization.total_delta_cents),
            _fac_parent_quantity: String(baseQuantity),
        };

        delete properties._fac_pricing_payload;

        return applyOptionalTemplateProperties(properties, authorization);
    }

    function buildNewSurchargeProperties(snapshot, authorization, baseQuantity) {
        return buildSurchargeProperties(
            {
                _fac_is_surcharge: '1',
                _fac_parent_variant_id: snapshot.base.variantId,
                _fac_parent_product_id: snapshot.base.productId,
                _fac_parent_save_token_hash_source: 'printess_save_token',
                _fac_printess_template_name:
                    snapshot.base.properties._fac_printess_template_name || '',
            },
            authorization,
            baseQuantity
        );
    }

    function buildBaseProperties(snapshotProperties, authorization) {
        if (!root.FacCartPricing?.filterProductionProperties) {
            fail('FAC production property filtering is unavailable.');
        }

        const printessProperties = BASE_PRINTESS_PROPERTIES.reduce((properties, propertyName) => {
            if (Object.prototype.hasOwnProperty.call(snapshotProperties, propertyName)) {
                properties[propertyName] = snapshotProperties[propertyName];
            }

            return properties;
        }, {});
        const useServerVariantMapping =
            snapshotProperties._fac_use_server_variant_mapping === '1';
        const properties = {
            ...printessProperties,
            ...root.FacCartPricing.filterProductionProperties(
                snapshotProperties,
                useServerVariantMapping
            ),
            _fac_is_base: '1',
            _fac_cart_group_id: authorization.cart_group_id,
            _fac_pricing_signature: authorization.pricing_signature,
            _fac_pricing_payload: authorization.pricing_payload,
            _fac_pricing_expires_at: String(authorization.expires_at),
            _fac_total_delta_cents: String(authorization.total_delta_cents),
        };

        if (authorization.surcharge_variant_id !== null) {
            properties._fac_surcharge_variant_id = authorization.surcharge_variant_id;
        }

        return applyOptionalTemplateProperties(properties, authorization);
    }

    function propertiesEqual(left, right) {
        const leftKeys = Object.keys(left).sort();
        const rightKeys = Object.keys(right).sort();

        if (leftKeys.length !== rightKeys.length) {
            return false;
        }

        return leftKeys.every((key, index) => {
            return key === rightKeys[index] && left[key] === right[key];
        });
    }

    function assertWrittenSurcharge(cart, authorization) {
        const matches = cart.items.filter((item) => {
            const properties = itemProperties(item);

            return (
                isFacSurcharge(item) &&
                properties._fac_cart_group_id === authorization.cart_group_id &&
                properties._fac_pricing_signature === authorization.pricing_signature
            );
        });

        if (matches.length !== 1) {
            fail('Updated surcharge line could not be identified.');
        }

        if (
            normalizeIdentifier(
                matches[0].variant_id ?? matches[0].id,
                'Surcharge variant ID'
            ) !== authorization.surcharge_variant_id ||
            matches[0].quantity !== authorization.surcharge_quantity
        ) {
            fail('Updated surcharge line does not match the authorization.');
        }

        return matches[0];
    }

    function assertBaseStillOnOldGroup(cart, snapshot) {
        const matches = cart.items.filter((item) => {
            return (
                isFacBase(item) &&
                itemProperties(item)._fac_cart_group_id === snapshot.cartGroupId
            );
        });

        if (matches.length !== 1) {
            fail('Base line changed unexpectedly before its authorization update.');
        }

        const baseItem = matches[0];

        if (
            normalizeIdentifier(baseItem.product_id, 'Shopify product ID') !==
                snapshot.base.productId ||
            normalizeIdentifier(
                baseItem.variant_id ?? baseItem.id,
                'Shopify variant ID'
            ) !== snapshot.base.variantId
        ) {
            fail('Base line identity changed unexpectedly before its authorization update.');
        }

        return baseItem;
    }

    function assertFinalInvariants(cart, snapshot, authorization, baseQuantity) {
        if (cart.items.some((item) => {
            return itemProperties(item)._fac_cart_group_id === snapshot.cartGroupId;
        })) {
            fail('The old cart group ID is still present.');
        }

        const pair = resolvePair(cart, authorization.cart_group_id);
        const baseProperties = itemProperties(pair.baseItem);
        const hasPositiveSurcharge = authorization.surcharge_quantity > 0;
        const surchargeProperties = pair.surchargeItem
            ? itemProperties(pair.surchargeItem)
            : null;

        if (pair.baseItem.quantity !== baseQuantity) {
            fail('Updated base quantity does not match the authorization.');
        }

        if (hasPositiveSurcharge) {
            if (!pair.surchargeItem) {
                fail('Updated surcharge line is missing.');
            }

            if (pair.surchargeItem.quantity !== authorization.surcharge_quantity) {
                fail('Updated surcharge quantity does not match the authorization.');
            }

            if (
                normalizeIdentifier(
                    pair.surchargeItem.variant_id ?? pair.surchargeItem.id,
                    'Surcharge variant ID'
                ) !== authorization.surcharge_variant_id
            ) {
                fail('Updated surcharge variant does not match the authorization.');
            }
        } else if (pair.surchargeItem) {
            fail('Zero-surcharge authorization must not contain a surcharge line.');
        }

        for (const properties of [baseProperties, surchargeProperties].filter(Boolean)) {
            if (properties._fac_pricing_signature !== authorization.pricing_signature) {
                fail('Updated pricing signature does not match the authorization.');
            }

            if (properties._fac_pricing_expires_at !== String(authorization.expires_at)) {
                fail('Updated pricing expiry does not match the authorization.');
            }

            if (properties._fac_total_delta_cents !== String(authorization.total_delta_cents)) {
                fail('Updated pricing delta does not match the authorization.');
            }

            if (
                properties._fac_printess_template_id !==
                    String(authorization.printess_template_id)
            ) {
                fail('Updated Printess template does not match the authorization.');
            }
        }

        if (baseProperties._fac_pricing_payload !== authorization.pricing_payload) {
            fail('Updated pricing payload does not match the authorization.');
        }

        if (authorization.surcharge_variant_id === null) {
            if (Object.prototype.hasOwnProperty.call(
                baseProperties,
                '_fac_surcharge_variant_id'
            )) {
                fail('Updated zero-surcharge base contains a surcharge variant.');
            }
        } else if (
            baseProperties._fac_surcharge_variant_id !== authorization.surcharge_variant_id
        ) {
            fail('Updated base surcharge variant does not match the authorization.');
        }

        if (surchargeProperties) {
            if (Object.prototype.hasOwnProperty.call(surchargeProperties, '_fac_pricing_payload')) {
                fail('Surcharge line must not contain a pricing payload.');
            }

            if (surchargeProperties._fac_parent_quantity !== String(baseQuantity)) {
                fail('Updated parent quantity does not match the base quantity.');
            }

            if (surchargeProperties._fac_parent_product_id !== snapshot.base.productId) {
                fail('Surcharge parent product no longer matches the base line.');
            }

            if (surchargeProperties._fac_parent_variant_id !== snapshot.base.variantId) {
                fail('Surcharge parent variant no longer matches the base line.');
            }
        }

        return pair;
    }

    function assertSnapshotRestored(cart, snapshot, authorization) {
        const pair = resolvePair(cart, snapshot.cartGroupId);

        if (pair.baseItem.quantity !== snapshot.base.quantity) {
            fail('Base quantity was not restored.');
        }

        if (!propertiesEqual(itemProperties(pair.baseItem), snapshot.base.properties)) {
            fail('Base properties were not restored.');
        }

        if (snapshot.surcharge) {
            if (!pair.surchargeItem) {
                fail('Surcharge line was not restored.');
            }

            if (pair.surchargeItem.quantity !== snapshot.surcharge.quantity) {
                fail('Surcharge quantity was not restored.');
            }

            if (
                normalizeIdentifier(
                    pair.surchargeItem.variant_id ?? pair.surchargeItem.id,
                    'Surcharge variant ID'
                ) !== snapshot.surcharge.variantId
            ) {
                fail('Surcharge variant was not restored.');
            }

            if (!propertiesEqual(itemProperties(pair.surchargeItem), snapshot.surcharge.properties)) {
                fail('Surcharge properties were not restored.');
            }
        } else if (pair.surchargeItem) {
            fail('Unexpected surcharge line remained after rollback.');
        }

        if (authorization && cart.items.some((item) => {
            return itemProperties(item)._fac_cart_group_id === authorization.cart_group_id;
        })) {
            fail('The failed authorization group is still present after rollback.');
        }

        return pair;
    }

    async function loadCart() {
        const response = await fetch(`${theme.routes.cart_url}.js`, {
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            fail('Cart could not be loaded.');
        }

        const cart = await response.json();

        if (!cart || !Array.isArray(cart.items)) {
            fail('Invalid cart state.');
        }

        return cart;
    }

    async function changeLine(key, quantity, properties, renderOptions = null) {
        const requestBody = {
            id: key,
            quantity,
            properties,
        };

        if (renderOptions) {
            if (!Array.isArray(renderOptions.sections) || renderOptions.sections.length === 0) {
                fail('Missing or invalid cart sections.');
            }

            requestBody.sections = renderOptions.sections;
            requestBody.sections_url = requireNonEmptyString(
                renderOptions.sections_url,
                'cart sections URL'
            );
        }

        const response = await fetch(`${theme.routes.cart_change_url}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });

        let result;

        try {
            result = await response.json();
        } catch (error) {
            fail('Shopify returned an invalid cart response.');
        }

        if (!response.ok || result.errors || result.status) {
            fail('Shopify cart line update failed.');
        }

        if (!result || !Array.isArray(result.items)) {
            fail('Shopify returned an invalid cart state.');
        }

        return result;
    }

    async function addSurchargeLine(variantId, quantity, properties) {
        const response = await fetch(`${theme.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                items: [{
                    id: variantId,
                    quantity,
                    properties,
                }],
            }),
        });

        let result;

        try {
            result = await response.json();
        } catch (error) {
            fail('Shopify returned an invalid cart response.');
        }

        if (!response.ok || result.errors || result.status) {
            fail('Shopify surcharge line add failed.');
        }

        return result;
    }

    async function applySurchargeTransition(
        cart,
        snapshot,
        authorization,
        transition,
        baseQuantity
    ) {
        if (transition === TRANSITION_NONE_TO_NONE) {
            return cart;
        }

        if (transition === TRANSITION_NONE_TO_SURCHARGE) {
            await addSurchargeLine(
                authorization.surcharge_variant_id,
                authorization.surcharge_quantity,
                buildNewSurchargeProperties(snapshot, authorization, baseQuantity)
            );
            const updatedCart = await loadCart();
            assertWrittenSurcharge(updatedCart, authorization);

            return updatedCart;
        }

        if (transition === TRANSITION_SAME_SURCHARGE) {
            const updatedCart = await changeLine(
                snapshot.surcharge.key,
                authorization.surcharge_quantity,
                buildSurchargeProperties(
                    snapshot.surcharge.properties,
                    authorization,
                    baseQuantity
                )
            );
            assertWrittenSurcharge(updatedCart, authorization);

            return updatedCart;
        }

        if (transition === TRANSITION_SURCHARGE_TO_NONE) {
            const updatedCart = await changeLine(
                snapshot.surcharge.key,
                0,
                snapshot.surcharge.properties
            );

            if (updatedCart.items.some((item) => {
                return itemProperties(item)._fac_cart_group_id === snapshot.cartGroupId &&
                    isFacSurcharge(item);
            })) {
                fail('Surcharge line could not be removed.');
            }

            return updatedCart;
        }

        if (transition !== TRANSITION_REPLACE_SURCHARGE) {
            fail('Unsupported surcharge transition.');
        }

        await addSurchargeLine(
            authorization.surcharge_variant_id,
            authorization.surcharge_quantity,
            buildNewSurchargeProperties(snapshot, authorization, baseQuantity)
        );

        let updatedCart = await loadCart();
        assertWrittenSurcharge(updatedCart, authorization);
        const oldPair = resolvePair(updatedCart, snapshot.cartGroupId);

        if (!oldPair.surchargeItem) {
            fail('Existing surcharge line disappeared before replacement.');
        }

        updatedCart = await changeLine(
            oldPair.surchargeItem.key,
            0,
            itemProperties(oldPair.surchargeItem)
        );

        if (updatedCart.items.some((item) => {
            return itemProperties(item)._fac_cart_group_id === snapshot.cartGroupId &&
                isFacSurcharge(item);
        })) {
            fail('Existing surcharge line could not be removed during replacement.');
        }

        assertWrittenSurcharge(updatedCart, authorization);

        return updatedCart;
    }

    function getSectionElement(html, selector, sectionName) {
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const selectedElement = parsedDocument.querySelector(selector);

        if (!selectedElement) {
            throw sectionRefreshError(`Cart section ${selector} could not be rendered.`, {
                phase: 'parse-section',
                sectionName,
            });
        }

        return selectedElement;
    }

    const SECTION_TARGETS = {
        'main-cart': {
            id: 'Cart',
            selector: '.thb-cart-form',
            requiresFacBaseLine: true,
        },
        'cart-drawer': {
            id: 'Cart-Drawer',
            selector: '.cart-drawer',
            requiresFacBaseLine: true,
        },
        'cart-bubble': {
            id: 'cart-drawer-toggle',
            selector: '.thb-item-count',
            requiresFacBaseLine: false,
        },
    };

    function containsExpectedFacBaseLine(container, expectation) {
        return Array.from(container.querySelectorAll('[data-fac-is-base="1"]')).some((element) => {
            return (
                element.dataset.facCartGroupId === expectation.cartGroupId &&
                element.dataset.key === expectation.baseKey
            );
        });
    }

    function facBaseLineIdentity(container, expectation) {
        const baseLines = Array.from(container.querySelectorAll('[data-fac-is-base="1"]'));

        return {
            groupMatch: baseLines.some((element) => {
                return element.dataset.facCartGroupId === expectation.cartGroupId;
            }),
            keyMatch: baseLines.some((element) => {
                return element.dataset.key === expectation.baseKey;
            }),
            exactMatch: containsExpectedFacBaseLine(container, expectation),
        };
    }

    function prepareSections(sections, expectation, sectionNames = SECTION_NAMES) {
        if (!sections || Array.isArray(sections) || typeof sections !== 'object') {
            throw sectionRefreshError('Shopify returned an invalid cart section response.', {
                phase: 'validate-sections-response',
            });
        }

        const preparedSections = {};

        for (const sectionName of sectionNames) {
            const html = sections[sectionName];
            const target = SECTION_TARGETS[sectionName];

            if (typeof html !== 'string' || html.trim().length === 0) {
                throw sectionRefreshError(
                    `Shopify cart section ${sectionName} is missing or invalid.`,
                    {
                        phase: 'validate-section',
                        sectionName,
                    }
                );
            }

            const selectedElement = getSectionElement(html, target.selector, sectionName);
            const identity = target.requiresFacBaseLine
                ? facBaseLineIdentity(selectedElement, expectation)
                : null;

            if (
                target.requiresFacBaseLine &&
                !identity.exactMatch
            ) {
                throw sectionRefreshError(
                    `Shopify cart section ${sectionName} does not match the final FAC cart state.`,
                    {
                        phase: 'validate-section-identity',
                        sectionName,
                        groupMatch: identity.groupMatch,
                        keyMatch: identity.keyMatch,
                    }
                );
            }

            if (
                target.requiresFacBaseLine &&
                expectation.baseQuantity !== undefined &&
                !containsExpectedFacBaseQuantity(selectedElement, expectation)
            ) {
                throw sectionRefreshError(
                    `Shopify cart section ${sectionName} has an invalid FAC base quantity.`,
                    {
                        phase: 'validate-section-quantity',
                        sectionName,
                        groupMatch: identity.groupMatch,
                        keyMatch: identity.keyMatch,
                    }
                );
            }

            preparedSections[sectionName] = selectedElement.innerHTML;
        }

        return preparedSections;
    }

    function replaceSection(sectionName, innerHTML) {
        const target = SECTION_TARGETS[sectionName];
        const currentElement = target ? document.getElementById(target.id) : null;

        if (!target || !currentElement) {
            throw sectionRefreshError(`Cart section target ${sectionName} is missing.`, {
                phase: 'replace-section-target',
                sectionName,
            });
        }

        const elementToReplace = currentElement.querySelector(target.selector) || currentElement;
        elementToReplace.innerHTML = innerHTML;
    }

    function assertRenderedSections(expectation, sectionNames = SECTION_NAMES) {
        for (const sectionName of sectionNames) {
            const target = SECTION_TARGETS[sectionName];
            const currentElement = document.getElementById(target.id);

            if (!currentElement) {
                throw sectionRefreshError(`Rendered cart section ${sectionName} is missing.`, {
                    phase: 'validate-rendered-section-target',
                    sectionName,
                });
            }

            if (target.requiresFacBaseLine) {
                const identity = facBaseLineIdentity(currentElement, expectation);

                if (!identity.exactMatch) {
                    throw sectionRefreshError(
                        `Rendered cart section ${sectionName} does not match the final FAC cart state.`,
                        {
                            phase: 'validate-rendered-section-identity',
                            sectionName,
                            groupMatch: identity.groupMatch,
                            keyMatch: identity.keyMatch,
                        }
                    );
                }

                if (
                    expectation.baseQuantity !== undefined &&
                    !containsExpectedFacBaseQuantity(currentElement, expectation)
                ) {
                    throw sectionRefreshError(
                        `Rendered cart section ${sectionName} has an invalid FAC base quantity.`,
                        {
                            phase: 'validate-rendered-section-quantity',
                            sectionName,
                            groupMatch: identity.groupMatch,
                            keyMatch: identity.keyMatch,
                        }
                    );
                }
            }
        }
    }

    function captureDrawerPresentation(sectionNames) {
        if (
            typeof document === 'undefined' ||
            !sectionNames.includes('cart-drawer')
        ) {
            return null;
        }

        const drawer = document.getElementById('Cart-Drawer');
        const scrollContainer = drawer?.querySelector('.side-panel-content');

        return {
            wasOpen: Boolean(drawer?.classList.contains('active')),
            bodyWasOpen: Boolean(document.body?.classList.contains('open-cc')),
            scrollTop: scrollContainer?.scrollTop ?? 0,
        };
    }

    function restoreDrawerPresentation(presentation) {
        if (!presentation || typeof document === 'undefined') {
            return;
        }

        const drawer = document.getElementById('Cart-Drawer');

        if (presentation.wasOpen) {
            drawer?.classList.add('active');
        }

        if (presentation.bodyWasOpen || presentation.wasOpen) {
            document.body?.classList.add('open-cc');
        }

        const scrollContainer = drawer?.querySelector('.side-panel-content');

        if (scrollContainer) {
            scrollContainer.scrollTop = presentation.scrollTop;
        }
    }

    function applyPreparedSections(preparedSections, expectation, sectionNames) {
        for (const sectionName of sectionNames) {
            const target = SECTION_TARGETS[sectionName];

            if (!target || !document.getElementById(target.id)) {
                throw sectionRefreshError(`Cart section target ${sectionName} is missing.`, {
                    phase: 'replace-section-target',
                    sectionName,
                });
            }
        }

        const drawerPresentation = captureDrawerPresentation(sectionNames);

        for (const sectionName of sectionNames) {
            replaceSection(sectionName, preparedSections[sectionName]);
        }

        restoreDrawerPresentation(drawerPresentation);
        assertRenderedSections(expectation, sectionNames);
        syncLockedControls();
    }

    function renderMutationSections(sections, generation, expectation, sectionNames) {
        assertCurrentGeneration(generation);
        const preparedSections = prepareSections(sections, expectation, sectionNames);
        assertCurrentGeneration(generation);
        applyPreparedSections(preparedSections, expectation, sectionNames);
    }

    async function refreshSections(
        generation,
        expectation,
        retryAttempt = 1,
        sectionNames = SECTION_NAMES
    ) {
        if (typeof document === 'undefined') {
            return;
        }

        const separator = theme.routes.cart_url.includes('?') ? '&' : '?';
        sectionRefreshNonce += 1;
        const cacheBuster = `${generation}-${sectionRefreshNonce}-${Date.now()}`;
        let response;

        try {
            response = await fetch(
                `${theme.routes.cart_url}${separator}sections=${sectionNames.join(',')}` +
                    `&fac_update=${encodeURIComponent(cacheBuster)}`,
                {
                    cache: 'no-store',
                    headers: {
                        'Accept': 'application/json',
                    },
                }
            );
        } catch (error) {
            throw sectionRefreshError('Cart sections could not be loaded.', {
                phase: 'fetch-sections',
            });
        }

        if (!response.ok) {
            throw sectionRefreshError('Cart sections could not be loaded.', {
                phase: 'fetch-sections',
            });
        }

        let preparedSections;

        try {
            const sections = await response.json();
            assertCurrentGeneration(generation);
            preparedSections = prepareSections(sections, expectation, sectionNames);
            assertCurrentGeneration(generation);
        } catch (error) {
            const refreshError = error?.facStaleGeneration || error?.facSectionRefresh
                ? error
                : sectionRefreshError('Cart sections could not be parsed.', {
                    phase: 'parse-sections-response',
                });
            throw refreshError;
        }

        applyPreparedSections(preparedSections, expectation, sectionNames);
    }

    function finalCartExpectation(pair, cartGroupId) {
        return {
            cartGroupId,
            baseKey: pair.baseItem.key,
            surchargeKey: pair.surchargeItem?.key || null,
            baseQuantity: pair.baseItem.quantity,
            surchargeQuantity: pair.surchargeItem?.quantity || 0,
        };
    }

    function cartMatchesFinalState(cart, expectation) {
        try {
            const pair = resolvePair(cart, expectation.cartGroupId);

            const surchargeMatches = expectation.surchargeKey === null
                ? pair.surchargeItem === null && expectation.surchargeQuantity === 0
                : Boolean(
                    pair.surchargeItem &&
                    pair.surchargeItem.key === expectation.surchargeKey &&
                    pair.surchargeItem.quantity === expectation.surchargeQuantity
                );

            return (
                pair.baseItem.key === expectation.baseKey &&
                pair.baseItem.quantity === expectation.baseQuantity &&
                surchargeMatches
            );
        } catch (error) {
            return false;
        }
    }

    function waitForSectionRetry(delayMs) {
        return new Promise((resolve) => {
            setTimeout(resolve, delayMs);
        });
    }

    async function refreshSectionsWithRetry(options) {
        const refresh = options.refreshSectionsFn || refreshSections;
        const readCart = options.loadCartFn || loadCart;
        const wait = options.waitFn || waitForSectionRetry;
        const maxAttempts = options.maxAttempts || SECTION_REFRESH_MAX_ATTEMPTS;
        const retryDelayMs = options.retryDelayMs ?? SECTION_REFRESH_RETRY_DELAY_MS;

        for (let retryAttempt = 1; retryAttempt <= maxAttempts; retryAttempt += 1) {
            try {
                await refresh(
                    options.generation,
                    options.sectionExpectation,
                    retryAttempt,
                    options.sectionNames || SECTION_NAMES
                );

                return { retryAttempt };
            } catch (error) {
                if (error?.facStaleGeneration) {
                    logSectionRefresh({
                        generation: options.generation,
                        retryAttempt,
                        phase: 'generation-check',
                        sectionName: null,
                        groupMatch: null,
                        keyMatch: null,
                        cartStillMatchesFinalState: null,
                    });
                    throw error;
                }

                let cartStillMatchesFinalState = null;

                try {
                    const currentCart = await readCart();
                    cartStillMatchesFinalState = cartMatchesFinalState(
                        currentCart,
                        options.finalCartExpectation
                    );
                } catch (cartReadError) {
                    cartStillMatchesFinalState = null;
                }

                logSectionRefresh({
                    generation: options.generation,
                    retryAttempt,
                    phase: error?.facPhase || 'verify-cart-after-section-failure',
                    sectionName: error?.facSectionName,
                    groupMatch: error?.facGroupMatch,
                    keyMatch: error?.facKeyMatch,
                    cartStillMatchesFinalState,
                });

                if (cartStillMatchesFinalState === false) {
                    const divergenceError = new Error(
                        'Cart no longer matches the validated final FAC state.'
                    );
                    divergenceError.facFinalCartDiverged = true;

                    throw divergenceError;
                }

                if (retryAttempt === maxAttempts) {
                    const exhaustedError = new Error(
                        'Cart sections remained stale after all refresh attempts.'
                    );
                    exhaustedError.facSectionRetriesExhausted = true;
                    exhaustedError.facFinalCartStillMatches =
                        cartStillMatchesFinalState === true;

                    throw exhaustedError;
                }

                await wait(retryDelayMs);
            }
        }
    }

    function triggerFullReload(reloadFn) {
        if (fullReloadTriggered) {
            return false;
        }

        const reload = reloadFn || (() => root.location.reload());
        fullReloadTriggered = true;
        reload();

        return true;
    }

    function containsExpectedFacBaseQuantity(container, expectation) {
        return Array.from(
            container.querySelectorAll('input[data-fac-is-base="1"]')
        ).some((element) => {
            return (
                element.dataset.facCartGroupId === expectation.cartGroupId &&
                element.dataset.key === expectation.baseKey &&
                String(element.value) === String(expectation.baseQuantity)
            );
        });
    }

    function lockElement(element) {
        if (element.dataset.facPricingLock === '1') {
            return;
        }

        element.dataset.facPricingLock = '1';
        element.dataset.facPricingPreviousAriaDisabled = element.getAttribute('aria-disabled') ?? '';
        element.setAttribute('aria-disabled', 'true');

        if ('disabled' in element) {
            element.dataset.facPricingPreviousDisabled = element.disabled ? '1' : '0';
            element.disabled = true;
        }

        if (element.matches('a, [role="button"]')) {
            element.dataset.facPricingPreviousTabindex = element.getAttribute('tabindex') ?? '';
            element.setAttribute('tabindex', '-1');
        }
    }

    function unlockElement(element) {
        if (element.dataset.facPricingLock !== '1') {
            return;
        }

        if ('disabled' in element && element.dataset.facPricingPreviousDisabled === '0') {
            element.disabled = false;
        }

        const previousAriaDisabled = element.dataset.facPricingPreviousAriaDisabled;

        if (previousAriaDisabled === '') {
            element.removeAttribute('aria-disabled');
        } else {
            element.setAttribute('aria-disabled', previousAriaDisabled);
        }

        if (element.matches('a, [role="button"]')) {
            const previousTabindex = element.dataset.facPricingPreviousTabindex;

            if (previousTabindex === '') {
                element.removeAttribute('tabindex');
            } else {
                element.setAttribute('tabindex', previousTabindex);
            }
        }

        delete element.dataset.facPricingLock;
        delete element.dataset.facPricingPreviousAriaDisabled;
        delete element.dataset.facPricingPreviousDisabled;
        delete element.dataset.facPricingPreviousTabindex;
    }

    function guardedElements() {
        if (typeof document === 'undefined') {
            return [];
        }

        return document.querySelectorAll([
            '#Cart input[name="updates[]"]',
            '#Cart quantity-selector button',
            '#Cart .remove',
            '#Cart .checkout-button',
            '#Cart-Drawer input[name="updates[]"]',
            '#Cart-Drawer quantity-selector button',
            '#Cart-Drawer .remove',
            '#Cart-Drawer .checkout-button',
            '.additional-checkout-buttons button',
            '.additional-checkout-buttons a',
        ].join(','));
    }

    function syncLockedControls() {
        for (const element of guardedElements()) {
            if (state === STATE_IDLE) {
                unlockElement(element);
            } else {
                lockElement(element);
            }
        }
    }

    function setState(nextState) {
        state = nextState;

        if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle(
                'fac-cart-pricing-updating',
                state === STATE_UPDATING
            );
            document.documentElement.classList.toggle(
                'fac-cart-pricing-blocked',
                state === STATE_BLOCKED
            );
        }

        syncLockedControls();
    }

    function showGlobalError(message) {
        if (typeof document === 'undefined') {
            return;
        }

        const containers = [
            document.querySelector('#Cart'),
            document.querySelector('#Cart-Drawer .side-panel-content'),
        ].filter(Boolean);

        for (const container of containers) {
            let notice = container.querySelector('[data-fac-cart-pricing-error]');

            if (!notice) {
                notice = document.createElement('div');
                notice.className = 'form-notification error fac-cart-pricing-error';
                notice.dataset.facCartPricingError = '1';
                notice.setAttribute('role', 'alert');
                container.prepend(notice);
            }

            notice.textContent = message;
        }
    }

    function clearGlobalError() {
        if (typeof document === 'undefined') {
            return;
        }

        document.querySelectorAll('[data-fac-cart-pricing-error]').forEach((notice) => {
            notice.remove();
        });
    }

    function isGuardedInteraction(target) {
        return Boolean(target && target.closest([
            '#Cart quantity-selector',
            '#Cart .remove',
            '#Cart .checkout-button',
            '#Cart-Drawer quantity-selector',
            '#Cart-Drawer .remove',
            '#Cart-Drawer .checkout-button',
            '.additional-checkout-buttons',
        ].join(',')));
    }

    function installGuards() {
        if (guardsInstalled || typeof document === 'undefined') {
            return;
        }

        guardsInstalled = true;

        document.addEventListener('click', (event) => {
            if (state !== STATE_IDLE && isGuardedInteraction(event.target)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        }, true);

        document.addEventListener('submit', (event) => {
            if (state === STATE_IDLE) {
                return;
            }

            const form = event.target;
            const action = form instanceof HTMLFormElement ? form.getAttribute('action') || '' : '';

            if (
                action.includes('/cart') ||
                event.submitter?.name === 'checkout' ||
                form?.id === 'cart-form'
            ) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        }, true);
    }

    function findWrittenLine(cart, authorization, marker) {
        const matches = cart.items.filter((item) => {
            const properties = itemProperties(item);
            const markerMatches = marker === 'base' ? isFacBase(item) : isFacSurcharge(item);

            return (
                markerMatches &&
                properties._fac_cart_group_id === authorization.cart_group_id &&
                properties._fac_pricing_signature === authorization.pricing_signature
            );
        });

        if (matches.length > 1) {
            fail(`Rollback ${marker} line is ambiguous.`);
        }

        return matches[0] || null;
    }

    function rollbackSurchargeAction(snapshot, writtenSurcharge) {
        if (writtenSurcharge && snapshot.surcharge) {
            const writtenVariantId = normalizeIdentifier(
                writtenSurcharge.variant_id ?? writtenSurcharge.id,
                'Surcharge variant ID'
            );

            return writtenVariantId === snapshot.surcharge.variantId
                ? 'restore'
                : 'replace';
        }

        if (writtenSurcharge) {
            return 'remove';
        }

        if (snapshot.surcharge) {
            return 'add';
        }

        return 'none';
    }

    async function rollback(snapshot, authorization) {
        let cart = await loadCart();
        const writtenBase = findWrittenLine(cart, authorization, 'base');

        if (writtenBase) {
            await changeLine(
                writtenBase.key,
                snapshot.base.quantity,
                snapshot.base.properties
            );
        }

        cart = await loadCart();

        const writtenSurcharge = findWrittenLine(cart, authorization, 'surcharge');
        const surchargeAction = rollbackSurchargeAction(snapshot, writtenSurcharge);

        if (surchargeAction === 'restore') {
            await changeLine(
                writtenSurcharge.key,
                snapshot.surcharge.quantity,
                snapshot.surcharge.properties
            );
        } else if (surchargeAction === 'remove') {
            await changeLine(
                writtenSurcharge.key,
                0,
                itemProperties(writtenSurcharge)
            );
        } else if (surchargeAction === 'replace') {
            await changeLine(
                writtenSurcharge.key,
                0,
                itemProperties(writtenSurcharge)
            );
        }

        cart = await loadCart();
        const restoredGroup = resolvePair(cart, snapshot.cartGroupId);

        if (snapshot.surcharge) {
            if (!restoredGroup.surchargeItem) {
                await addSurchargeLine(
                    snapshot.surcharge.variantId,
                    snapshot.surcharge.quantity,
                    snapshot.surcharge.properties
                );
            } else {
                const restoredVariantId = normalizeIdentifier(
                    restoredGroup.surchargeItem.variant_id ?? restoredGroup.surchargeItem.id,
                    'Surcharge variant ID'
                );

                if (restoredVariantId !== snapshot.surcharge.variantId) {
                    fail('Rollback found an unexpected surcharge variant.');
                }

                if (
                    restoredGroup.surchargeItem.quantity !== snapshot.surcharge.quantity ||
                    !propertiesEqual(
                        itemProperties(restoredGroup.surchargeItem),
                        snapshot.surcharge.properties
                    )
                ) {
                    await changeLine(
                        restoredGroup.surchargeItem.key,
                        snapshot.surcharge.quantity,
                        snapshot.surcharge.properties
                    );
                }
            }
        } else if (restoredGroup.surchargeItem) {
            await changeLine(
                restoredGroup.surchargeItem.key,
                0,
                itemProperties(restoredGroup.surchargeItem)
            );
        }

        cart = await loadCart();
        assertSnapshotRestored(cart, snapshot, authorization);

        return cart;
    }

    async function recoverBeforeMutation(generation, expectation, sectionNames) {
        try {
            await refreshSections(generation, expectation, 1, sectionNames);
            setState(STATE_IDLE);
            showGlobalError(UPDATE_ERROR_MESSAGE);

            return false;
        } catch (error) {
            setState(STATE_BLOCKED);
            showGlobalError(BLOCKED_ERROR_MESSAGE);

            return true;
        }
    }

    async function performUpdate(options, generation) {
        let snapshot = null;
        let authorization = null;
        let mutationAttempted = false;
        let updateOrigin = null;
        let validatedFinalExpectation = null;

        try {
            updateOrigin = requireUpdateOrigin(options?.origin);
            const cartGroupId = requireNonEmptyString(options?.cartGroupId, 'cart group ID');
            const baseKey = requireNonEmptyString(options?.baseKey, 'base line key');
            const baseQuantity = parseInteger(options?.quantity, 'quantity', 1);
            const cart = await loadCart();
            const pair = resolvePair(cart, cartGroupId, baseKey);

            snapshot = createSnapshot(pair, cartGroupId);

            if (snapshot.surcharge) {
                if (
                    snapshot.surcharge.properties._fac_parent_product_id !==
                        snapshot.base.productId
                ) {
                    fail('Surcharge parent product does not match the base line.');
                }

                if (
                    snapshot.surcharge.properties._fac_parent_variant_id !==
                        snapshot.base.variantId
                ) {
                    fail('Surcharge parent variant does not match the base line.');
                }
            }

            const validationRequest = buildValidationRequest(
                pair.baseItem,
                baseQuantity,
                snapshot.authorization.selected_options
            );

            if (!root.FacCartPricing || typeof root.FacCartPricing.validate !== 'function') {
                fail('FAC cart pricing validation is not available.');
            }

            const validationResult = await root.FacCartPricing.validate(validationRequest);
            authorization = validateAuthorizationResult(
                validationResult,
                snapshot,
                cart,
                baseQuantity
            );
            const transition = surchargeTransition(snapshot, authorization);
            mutationAttempted = transition !== TRANSITION_NONE_TO_NONE;
            const cartBeforeBaseUpdate = await applySurchargeTransition(
                cart,
                snapshot,
                authorization,
                transition,
                baseQuantity
            );

            const currentBase = assertBaseStillOnOldGroup(cartBeforeBaseUpdate, snapshot);
            const baseProperties = buildBaseProperties(snapshot.base.properties, authorization);
            const sectionNames = sectionNamesForOrigin(updateOrigin);

            mutationAttempted = true;
            const finalCart = await changeLine(currentBase.key, baseQuantity, baseProperties, {
                sections: sectionNames,
                sections_url: sectionsUrlForOrigin(updateOrigin),
            });

            const finalPair = assertFinalInvariants(
                finalCart,
                snapshot,
                authorization,
                baseQuantity
            );

            const sectionExpectation = {
                cartGroupId: authorization.cart_group_id,
                baseKey: finalPair.baseItem.key,
                baseQuantity: finalPair.baseItem.quantity,
            };

            validatedFinalExpectation = finalCartExpectation(
                finalPair,
                authorization.cart_group_id
            );

            try {
                renderMutationSections(
                    finalCart.sections,
                    generation,
                    sectionExpectation,
                    sectionNames
                );
            } catch (renderError) {
                if (renderError?.facStaleGeneration) {
                    throw renderError;
                }

                logSectionRefresh({
                    generation,
                    retryAttempt: 0,
                    phase: renderError?.facPhase || 'render-mutation-sections',
                    sectionName: renderError?.facSectionName,
                    groupMatch: renderError?.facGroupMatch,
                    keyMatch: renderError?.facKeyMatch,
                    cartStillMatchesFinalState: true,
                });

                await refreshSectionsWithRetry({
                    generation,
                    sectionExpectation,
                    finalCartExpectation: validatedFinalExpectation,
                    sectionNames,
                });
            }
            clearGlobalError();
            setState(STATE_IDLE);

            return {
                ignored: false,
                cart: finalCart,
            };
        } catch (error) {
            if (error?.facStaleGeneration) {
                throw error;
            }

            if (
                error?.facSectionRetriesExhausted &&
                error?.facFinalCartStillMatches === true
            ) {
                clearGlobalError();
                triggerFullReload();

                return {
                    ignored: true,
                    reloading: true,
                    cart: null,
                };
            }

            let blocked = false;

            if (mutationAttempted && snapshot && authorization) {
                try {
                    const restoredCart = await rollback(snapshot, authorization);
                    const restoredPair = resolvePair(restoredCart, snapshot.cartGroupId);

                    await refreshSections(
                        generation,
                        {
                            cartGroupId: snapshot.cartGroupId,
                            baseKey: restoredPair.baseItem.key,
                            baseQuantity: restoredPair.baseItem.quantity,
                        },
                        1,
                        updateOrigin
                            ? sectionNamesForOrigin(updateOrigin)
                            : SECTION_NAMES
                    );
                    setState(STATE_IDLE);
                    showGlobalError(UPDATE_ERROR_MESSAGE);
                } catch (rollbackError) {
                    logSectionRefresh({
                        generation,
                        retryAttempt: null,
                        phase: 'rollback-failed',
                        sectionName: null,
                        groupMatch: null,
                        keyMatch: null,
                        cartStillMatchesFinalState: null,
                    });
                    blocked = true;
                    setState(STATE_BLOCKED);
                    showGlobalError(BLOCKED_ERROR_MESSAGE);
                }
            } else {
                const recoveryExpectation = snapshot
                    ? {
                        cartGroupId: snapshot.cartGroupId,
                        baseKey: snapshot.base.key,
                        baseQuantity: snapshot.base.quantity,
                    }
                    : {
                        cartGroupId: String(options?.cartGroupId || ''),
                        baseKey: String(options?.baseKey || ''),
                    };

                blocked = await recoverBeforeMutation(
                    generation,
                    recoveryExpectation,
                    updateOrigin
                        ? sectionNamesForOrigin(updateOrigin)
                        : SECTION_NAMES
                );
            }

            const publicError = new Error(
                blocked ? BLOCKED_ERROR_MESSAGE : UPDATE_ERROR_MESSAGE
            );
            publicError.facCartPricingBlocked = blocked;

            throw publicError;
        }
    }

    function updateQuantity(options) {
        installGuards();

        if (state === STATE_BLOCKED) {
            const blockedError = new Error(BLOCKED_ERROR_MESSAGE);
            blockedError.facCartPricingBlocked = true;

            return Promise.reject(blockedError);
        }

        if (activeUpdate || state === STATE_UPDATING) {
            return Promise.resolve({
                ignored: true,
                cart: null,
            });
        }

        const generation = nextUpdateGeneration();
        setState(STATE_UPDATING);

        activeUpdate = performUpdate(options, generation).finally(() => {
            activeUpdate = null;
        });

        return activeUpdate;
    }

    function markPending() {
        installGuards();

        if (state === STATE_IDLE) {
            setState(STATE_PENDING);
        }
    }

    installGuards();

    root.FacDynamicPricingCartUpdate = {
        markPending,
        updateQuantity,
        getState() {
            return state;
        },
        isBlocked() {
            return state === STATE_BLOCKED;
        },
        isUpdating() {
            return state === STATE_UPDATING;
        },
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            resolvePair,
            buildValidationRequest,
            parseExistingAuthorization,
            createSnapshot,
            validateAuthorizationResult,
            surchargeTransition,
            applySurchargeTransition,
            buildSurchargeProperties,
            buildNewSurchargeProperties,
            buildBaseProperties,
            assertWrittenSurcharge,
            assertBaseStillOnOldGroup,
            assertFinalInvariants,
            assertSnapshotRestored,
            findWrittenLine,
            rollbackSurchargeAction,
            rollback,
            propertiesEqual,
            nextUpdateGeneration,
            isCurrentGeneration,
            assertCurrentGeneration,
            containsExpectedFacBaseLine,
            containsExpectedFacBaseQuantity,
            prepareSections,
            applyPreparedSections,
            renderMutationSections,
            changeLine,
            addSurchargeLine,
            sectionNamesForOrigin,
            sectionsUrlForOrigin,
            finalCartExpectation,
            cartMatchesFinalState,
            refreshSectionsWithRetry,
            triggerFullReload,
            TRANSITION_NONE_TO_NONE,
            TRANSITION_NONE_TO_SURCHARGE,
            TRANSITION_SURCHARGE_TO_NONE,
            TRANSITION_SAME_SURCHARGE,
            TRANSITION_REPLACE_SURCHARGE,
        };
    }
})(typeof window !== 'undefined' ? window : globalThis);
